import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Flex,
  message,
  Row,
  Col,
  Tag,
  Tooltip,
  Divider,
  Select,
  Input,
  RadioChangeEvent,
} from 'antd';
// api
import {
  ResetAlertToDefault,
  UpdateSingleAlert,
  UpdateSingleAlertPayload,
} from '../../../../apis/settings.js';

// helpers
import { useSpin } from '../../../../components/SpinContext.jsx';
import { recipientList } from '../../../../common/static_data';
import { truncateByLength, useToken } from '../../../../common/helper';
// icons
import {
  ChangelogIcon,
  DeleteIcon,
  LeftArrowIcon,
  OffIcon,
  ResetIcon,
  RightArrowIcon,
  SignalIcon,
} from '../../../../assets/icons/icons.jsx';
import { WarningFilled } from '@ant-design/icons';
// components
import {
  CustomInput,
  CustomParagraph,
  CustomTitle,
  ToggleSwitch,
  AlertCardPreview,
  CustomButton,
  AddItemButton,
  TruncateWithTooltip,
  CustomRadioGroup,
  AddSelectableItem,
} from '../../../../components';
import CustomDeleteModal from '../../../Modals/CustomDeleteModal.jsx';
import theme from '@/assets/theme';
import SortableActions from './SortableActions.js';
import AdvancedSettingsToggle from './AdvancedSettingsToggle';
import { getAlertDisplayName } from '@/common/alertType';
import './AlertCustomizationForm.css';
import {
  ActionItem,
  AlertCustomizationFormProps,
  FormValues,
  ToggleEvent,
} from '../type.js';

const isDefined = <T,>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

/**
 * Alert customization form component for configuring alert thresholds, contacts, and actions
 *
 * @param onClose - Callback to close the form
 * @param refreshAlerts - Callback to refresh the alerts list
 * @param selectedAlertData - The alert data to customize
 * @param selectedTeam - The selected team ID or 'all'
 */
const AlertCustomizationForm: React.FC<AlertCustomizationFormProps> = ({
  refreshAlerts,
  selectedAlertData,
  selectedTeam,
  patchedConfigJSON,
  AlertCustomizationPermissions,
  setDrawerState,
  actionsList,
  tpSelected,
  setTpSelected,
  isDirty,
  setIsDirty,
  setShowDrawer,
  showDiscardModal,
  setShowDiscardModal,

  selectedAction,
  setSelectedAction,
}) => {
  const { useWatch } = Form;
  const { TextArea } = Input;
  const action = 'gather_information';
  const [currentAlert, setCurrentAlert] = useState(selectedAlertData);

  const [showThreshold2, setShowThreshold2] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const IS_TP_EXCEPTION =
    (currentAlert?.phase_display ?? '').toLocaleLowerCase() ==
    'transport provider exceptions';
  const IS_SENSOR_STILL_AT_PICKUP =
    currentAlert?.alert_type === 'sensor_location_still_at_pickup';
  const IS_SENSOR_NOT_AT_DROP =
    currentAlert?.alert_type === 'sensor_location_not_at_drop';
  const IS_TEMPERATURE_EXCURSION =
    currentAlert?.alert_type === 'temperature_excursion_detected';
  const IS_TEMPERATURE_PRE_ALARM =
    currentAlert?.alert_type === 'temperature_excursion_pre_alarm';
  const IS_TEMPERATURE_ADVANCED =
    IS_TEMPERATURE_EXCURSION || IS_TEMPERATURE_PRE_ALARM;
  const { token, removeToken, decodedUserToken } = useToken();
  // Controlling office (the shipment's WC RTG office) exists only for Customer
  // Leg companies; it does not depend on the alert type.
  const SUPPORTS_CONTROLLING_OFFICE = Boolean(
    decodedUserToken()?.enablecustomerleg
  );
  // Keep a saved Controlling office selectable so it can be seen and cleared
  // (otherwise the hidden choice would disable every other contact option).
  const HAS_SAVED_CONTROLLING_OFFICE = (
    currentAlert?.recommended_contacts ?? []
  ).some((contact) => contact?.id === 'CONTROLLING_OFFICE');
  const SUPPORTS_THRESHOLD_VALUE_2 =
    currentAlert?.alert_type === 'low_battery' ||
    IS_SENSOR_STILL_AT_PICKUP ||
    IS_SENSOR_NOT_AT_DROP ||
    IS_TEMPERATURE_PRE_ALARM;
  const canNotSaveSettings =
    selectedAction === 'resolve_issue' &&
    IS_TP_EXCEPTION &&
    tpSelected?.length === 0;
  // TP exception recommended Action
  const handleActionChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setSelectedAction?.(value);
    setIsDirty?.(true);
  };

  const [_actionChange, setActionChange] = useState('');
  const [form] = Form.useForm();
  const recommendedAction = useWatch('recommendedAction', form);
  const [isActionFocused, setIsActionFocused] = useState(false);

  const processedList = recipientList
    .filter((recipient) => {
      if (recipient.id === 'CONTROLLING_OFFICE') {
        return SUPPORTS_CONTROLLING_OFFICE || HAS_SAVED_CONTROLLING_OFFICE;
      }
      return true;
    })
    .map((recipient) => {
      if (
        theme?.themeType === 2 &&
        recipient.name.includes('Transport provider')
      ) {
        return {
          ...recipient,
          name: recipient.name.replace('Transport provider', 'Carrier'),
        };
      }
      return recipient;
    });

  const { showSpin, hideSpin } = useSpin();
  const navigate = useNavigate();
  const title = getAlertDisplayName(
    currentAlert?.alert_type,
    currentAlert?.alert_name
  );

  const [isChecked, setIsChecked] = useState(currentAlert?.is_enabled || false);

  const allClickableAlerts = useMemo(() => {
    return patchedConfigJSON.flatMap((group) =>
      group.alerts.filter((record) => {
        const isDisabled =
          record?.alert_type === 'transport_provider_exception' ||
          record?.sensor_status === 'setup_required' ||
          !(
            AlertCustomizationPermissions?.is_create ||
            AlertCustomizationPermissions?.is_update
          );

        return !isDisabled; // keep only enabled
      })
    );
  }, [patchedConfigJSON, AlertCustomizationPermissions]);

  // Update new alert in the list
  useEffect(() => {
    setCurrentAlert(selectedAlertData);
  }, [selectedAlertData]);

  // Find index of the currently opened alert in the filtered list
  const currentIndex = allClickableAlerts.findIndex(
    (a) => a.id === currentAlert?.id // compare by ID or unique key
  );
  const goNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    const nextAlert = allClickableAlerts[nextIndex];

    if (nextAlert) {
      setCurrentAlert(nextAlert);
      setSelectedAction?.(nextAlert.recommended_action_type || action);
      setTpSelected?.(
        (nextAlert.preferred_actions || []).filter((item) => item.id != null)
      );
    }
  }, [
    currentIndex,
    allClickableAlerts,
    action,
    setSelectedAction,
    setTpSelected,
  ]);

  const goPrev = useCallback(() => {
    const prevIndex = currentIndex - 1;
    const prevAlert = allClickableAlerts[prevIndex];

    if (prevAlert) {
      setCurrentAlert(prevAlert);
      setSelectedAction?.(prevAlert.recommended_action_type || action);
      setTpSelected?.(
        (prevAlert.preferred_actions || []).filter((item) => item.id != null)
      );
    }
  }, [
    currentIndex,
    allClickableAlerts,
    action,
    setSelectedAction,
    setTpSelected,
  ]);

  const handleNext = () => {
    if (isDirty) {
      setPendingNav('next');
      setShowDiscardModal?.(true);
      return;
    }
    goNext();
  };

  const handlePrevious = () => {
    if (isDirty) {
      setPendingNav('prev');
      setShowDiscardModal?.(true);
      return;
    }
    goPrev();
  };

  useEffect(() => {
    if (currentAlert) {
      form.setFieldsValue({
        threshold: currentAlert?.threshold_value || '',
        threshold2: currentAlert?.threshold_value_2 || '',
        ...(IS_SENSOR_STILL_AT_PICKUP
          ? {
              departureGracePlanned:
                currentAlert?.advanced_settings?.departure_grace_planned_mins ??
                60,
              gpsAccuracyPadding:
                currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
            }
          : IS_SENSOR_NOT_AT_DROP
            ? {
                arrivalGracePlanned:
                  currentAlert?.advanced_settings?.arrival_grace_planned_mins ??
                  15,
                stopCheckActual:
                  currentAlert?.advanced_settings?.stop_check_actual_mins ?? 0,
                stopCheckPlanned:
                  currentAlert?.advanced_settings?.stop_check_planned_mins ?? 0,
                gpsAccuracyPadding:
                  currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
              }
            : IS_TEMPERATURE_ADVANCED
              ? {
                  lowerBoundPadding:
                    currentAlert?.advanced_settings
                      ?.lower_bounds_degree_padding ?? 0,
                  upperBoundPadding:
                    currentAlert?.advanced_settings
                      ?.upper_bounds_degree_padding ?? 0,
                  prePickupSkip:
                    currentAlert?.advanced_settings
                      ?.pre_collection_skip_count ??
                    (IS_TEMPERATURE_PRE_ALARM ? 3 : 0),
                  ...(IS_TEMPERATURE_PRE_ALARM
                    ? {
                        rollingAverageHours:
                          currentAlert?.advanced_settings
                            ?.rolling_average_hours ?? 5,
                        rateOfChangeFactor:
                          currentAlert?.advanced_settings
                            ?.rate_of_change_factor ?? 1,
                        sensorDataStartDelay:
                          currentAlert?.advanced_settings
                            ?.sensor_data_start_delay_minutes ?? 300,
                      }
                    : {}),
                }
              : {
                  departureGracePlanned: undefined,
                  arrivalGracePlanned: undefined,
                  stopCheckActual: undefined,
                  stopCheckPlanned: undefined,
                  gpsAccuracyPadding: undefined,
                  lowerBoundPadding: undefined,
                  upperBoundPadding: undefined,
                  prePickupSkip: undefined,
                }),
        recommendedContacts: currentAlert?.recommended_contacts?.map(
          (item) => item.id || item.value
        ),
        recommendedAction: currentAlert?.recommended_action || '',
      });
      setIsChecked(currentAlert?.is_enabled || false);
      setShowThreshold2(
        IS_SENSOR_STILL_AT_PICKUP ||
          IS_SENSOR_NOT_AT_DROP ||
          IS_TEMPERATURE_PRE_ALARM ||
          !!currentAlert?.threshold_value_2
      );
      setAdvancedSettingsOpen(false);
    }
  }, [
    currentAlert,
    form,
    IS_SENSOR_STILL_AT_PICKUP,
    IS_SENSOR_NOT_AT_DROP,
    IS_TEMPERATURE_ADVANCED,
    IS_TEMPERATURE_PRE_ALARM,
  ]);

  const handleToggle = (e: ToggleEvent): void => {
    setIsChecked(e.target.checked);
    const model = {
      team: selectedTeam || '',
      all_team: !selectedTeam || selectedTeam == 'all',
      alert_type: currentAlert?.alert_type,
      is_enabled: e.target.checked,
    };

    UpdateSingleAlert(model, token ?? '')
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            refreshAlerts();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        // hideSpin();
      });
  };

  const unauthorize = (status: number): void => {
    if (status === 401) {
      removeToken();
      const _ = navigate('/login');
    }
  };

  const onFinish = async (values: FormValues): Promise<void> => {
    if (canNotSaveSettings) {
      message.error('Please add a resolution option');
      return;
    }

    showSpin();
    const TP_preferred_payload = tpSelected?.map((action_type, index) => ({
      action_type: action_type?.action_type,
      priority: index,
    }));
    const hasThreshold2 =
      values?.threshold2 !== null &&
      values?.threshold2 !== undefined &&
      values?.threshold2 !== '';
    const model: UpdateSingleAlertPayload = {
      team: selectedTeam || '',
      all_team: !selectedTeam || selectedTeam == 'all',
      alert_type: currentAlert?.alert_type,
      threshold_value: IS_TP_EXCEPTION
        ? 'N/A'
        : currentAlert.alert_type === 'vehicle_idle_in_transit'
          ? Number(values.threshold) * 60
          : values?.threshold || 0,

      is_enabled: isChecked,
      recommended_contacts: Array.isArray(selectedContact)
        ? selectedContact
        : selectedContact
          ? [selectedContact]
          : [],
      recommended_action: values?.recommendedAction || '',
      // in case TP exception
      recommended_action_type: selectedAction || '',
      ...(IS_TP_EXCEPTION && { preferred_actions: TP_preferred_payload || [] }),
      ...(SUPPORTS_THRESHOLD_VALUE_2 && {
        threshold_value_2: hasThreshold2 ? values.threshold2 : null,
      }),
    };

    if (IS_SENSOR_STILL_AT_PICKUP) {
      model.advanced_settings = {
        departure_grace_planned_mins: Number(
          values.departureGracePlanned ?? 60
        ),
        gps_accuracy_padding: Number(values.gpsAccuracyPadding ?? 0.5),
      };
    }
    if (IS_SENSOR_NOT_AT_DROP) {
      model.advanced_settings = {
        arrival_grace_planned_mins: Number(values.arrivalGracePlanned ?? 15),
        stop_check_actual_mins: Number(values.stopCheckActual ?? 0),
        stop_check_planned_mins: Number(values.stopCheckPlanned ?? 0),
        gps_accuracy_padding: Number(values.gpsAccuracyPadding ?? 0.5),
      };
    }
    if (IS_TEMPERATURE_EXCURSION) {
      model.advanced_settings = {
        pre_collection_skip_count: Number(values.prePickupSkip ?? 0),
        lower_bounds_degree_padding: Number(values.lowerBoundPadding ?? 0),
        upper_bounds_degree_padding: Number(values.upperBoundPadding ?? 0),
      };
    }
    if (IS_TEMPERATURE_PRE_ALARM) {
      model.advanced_settings = {
        rolling_average_hours: Number(values.rollingAverageHours ?? 5),
        rate_of_change_factor: Number(values.rateOfChangeFactor ?? 1),
        pre_collection_skip_count: Number(values.prePickupSkip ?? 3),
        lower_bounds_degree_padding: Number(values.lowerBoundPadding ?? 0),
        upper_bounds_degree_padding: Number(values.upperBoundPadding ?? 0),
        sensor_data_start_delay_minutes: Number(
          values.sensorDataStartDelay ?? 300
        ),
      };
    }
    UpdateSingleAlert(model, token ?? '')
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            form.resetFields();
            refreshAlerts();
            setShowDrawer?.(false);
            setIsDirty?.(false);
          } else {
            message.error(data?.data?.message);
            hideSpin();
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  };
  const [showResetModal, setShowResetModal] = useState(false);

  const handleConfirmReset = () => {
    setShowResetModal(false);
    if (currentAlert && currentAlert.id) {
      showSpin();
      const model = {
        team: selectedTeam || '',
        all_team: !selectedTeam || selectedTeam == 'all',
        alert_type: currentAlert?.alert_type,
      };
      ResetAlertToDefault(model, token ?? '')
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              const resetPayload = data?.data?.data;
              setCurrentAlert(resetPayload);
              const resetValues: FormValues = {
                threshold: resetPayload.threshold_value || '',
                threshold2: resetPayload.threshold_value_2 || '',
                ...(resetPayload.alert_type === 'sensor_location_not_at_drop'
                  ? {
                      arrivalGracePlanned:
                        resetPayload.advanced_settings
                          ?.arrival_grace_planned_mins ?? 15,
                      stopCheckActual:
                        resetPayload.advanced_settings
                          ?.stop_check_actual_mins ?? 0,
                      stopCheckPlanned:
                        resetPayload.advanced_settings
                          ?.stop_check_planned_mins ?? 0,
                      gpsAccuracyPadding:
                        resetPayload.advanced_settings?.gps_accuracy_padding ??
                        0.5,
                    }
                  : resetPayload.alert_type ===
                      'sensor_location_still_at_pickup'
                    ? {
                        departureGracePlanned:
                          resetPayload.advanced_settings
                            ?.departure_grace_planned_mins ?? 60,
                        gpsAccuracyPadding:
                          resetPayload.advanced_settings
                            ?.gps_accuracy_padding ?? 0.5,
                      }
                    : resetPayload.alert_type ===
                        'temperature_excursion_detected'
                      ? {
                          lowerBoundPadding:
                            resetPayload.advanced_settings
                              ?.lower_bounds_degree_padding ?? 0,
                          upperBoundPadding:
                            resetPayload.advanced_settings
                              ?.upper_bounds_degree_padding ?? 0,
                          prePickupSkip:
                            resetPayload.advanced_settings
                              ?.pre_collection_skip_count ?? 0,
                        }
                      : resetPayload.alert_type ===
                          'temperature_excursion_pre_alarm'
                        ? {
                            rollingAverageHours:
                              resetPayload.advanced_settings
                                ?.rolling_average_hours ?? 5,
                            rateOfChangeFactor:
                              resetPayload.advanced_settings
                                ?.rate_of_change_factor ?? 1,
                            lowerBoundPadding:
                              resetPayload.advanced_settings
                                ?.lower_bounds_degree_padding ?? 0,
                            upperBoundPadding:
                              resetPayload.advanced_settings
                                ?.upper_bounds_degree_padding ?? 0,
                            prePickupSkip:
                              resetPayload.advanced_settings
                                ?.pre_collection_skip_count ?? 3,
                            sensorDataStartDelay:
                              resetPayload.advanced_settings
                                ?.sensor_data_start_delay_minutes ?? 300,
                          }
                        : {
                            departureGracePlanned: undefined,
                            arrivalGracePlanned: undefined,
                            stopCheckActual: undefined,
                            stopCheckPlanned: undefined,
                            gpsAccuracyPadding: undefined,
                            lowerBoundPadding: undefined,
                            upperBoundPadding: undefined,
                            prePickupSkip: undefined,
                          }),
                recommendedContacts: resetPayload.recommended_contacts
                  ?.map((item) => item.id || item.value)
                  .filter(isDefined),
                recommendedAction: resetPayload.recommended_action || '',
              };
              form.setFieldsValue(resetValues);
              setInitialValues(resetValues);
              setPreviewFormValues(resetValues);
              setIsDirty?.(false);
              message.success(data?.data?.message);
              refreshAlerts();
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  // One save writes a changelog row per changed field with the same author and
  // minute-resolution timestamp, so the occurrence count keeps keys unique.
  const changelogKeyCounts = new Map<string, number>();
  const changelogRows = (currentAlert?.changelog ?? []).map((entry) => {
    const base = `${entry.updated_by_name}|${entry.updated_by_title}|${entry.formatted_updated_at}`;
    const occurrence = (changelogKeyCounts.get(base) ?? 0) + 1;
    changelogKeyCounts.set(base, occurrence);
    return { entry, key: `${base}|${occurrence}` };
  });

  const ChangelogContent = () => (
    <>
      <CustomParagraph
        text={'Changelog'}
        noText='--'
        fontSize='12px'
        lineHeight='16px'
        fontWeight={600}
        color='var(--text-secondary)'
        marginBottom={0}
      />

      <Divider style={{ margin: '8px 0' }} />

      {changelogRows.map(({ entry, key }) => (
        <Flex
          key={key}
          gap={8}
          align='center'
          justify='space-between'
          style={{ marginBottom: '4px' }}
        >
          <Flex gap={4} align='center'>
            <CustomParagraph
              text={truncateByLength(entry?.updated_by_name, 12) + ','}
              noText='--'
              fontSize='10px'
              lineHeight='16px'
              fontWeight={600}
              color='var(--text-primary)'
              marginBottom={0}
            />

            <CustomParagraph
              text={truncateByLength(entry?.updated_by_title, 16)}
              noText='--'
              fontSize='10px'
              lineHeight='16px'
              fontWeight={400}
              color='var(--text-primary)'
              marginBottom={0}
            />
          </Flex>
          <CustomParagraph
            text={entry.formatted_updated_at}
            noText='--'
            fontSize='10px'
            lineHeight='16px'
            fontWeight={400}
            color='var(--text-secondary)'
            marginBottom={0}
          />
        </Flex>
      ))}
    </>
  );

  const divRef = useRef<HTMLDivElement>(null);
  const [divHeight, setDivHeight] = useState(0);
  useEffect(() => {
    if (!divRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0].contentRect.height;
      setDivHeight(height);
    });
    observer.observe(divRef.current);
    return () => observer.disconnect();
  }, []);

  const [selectedContact, setSelectedContact] = useState<string[] | null>([]);

  useEffect(() => {
    setSelectedContact(
      currentAlert?.recommended_contacts?.[0]?.id
        ? [currentAlert.recommended_contacts[0].id]
        : null
    );
  }, [currentAlert]);

  // Generate options: full label in dropdown, cleaned label in input
  const modifiedRecipientList = useMemo(() => {
    return processedList.map((item) => ({
      label: item.name, // full label in dropdown
      value: item.id,
      inputLabel: item.name.replace(/ primary contact/i, ''), // cleaned for selected display
      disabled:
        selectedContact !== null &&
        selectedContact.length > 0 &&
        !selectedContact.includes(item.id),
    }));
  }, [selectedContact, processedList]);

  // Helper to replace placeholders in template
  const getBannerText = useCallback(
    (tpl: string, values: FormValues) => {
      if (!tpl) return '';

      return tpl
        .replace(
          /\{\{threshold_value\}\}/g,
          String(values.threshold || currentAlert?.threshold_value || '')
        )
        .replace(/\{\{recommended_contact\}\}/g, () => {
          const selected = values.recommendedContacts?.[0];
          if (!selected) return '';

          const contact = processedList.find((r) => r.id === selected);
          if (!contact) return '';

          return contact.name
            ?.replace(/_/g, ' ')
            ?.toLowerCase()
            ?.replace(/\bprimary( contact)?\b/g, '')
            ?.trim();
        });
    },
    [currentAlert, processedList]
  );

  // Get current form values for preview
  const [previewFormValues, setPreviewFormValues] = useState(
    form.getFieldsValue()
  );

  useEffect(() => {
    // Run once after mount and when form is populated
    const initialValues = form.getFieldsValue();
    setPreviewFormValues(initialValues);
  }, [form]);

  // in case resolve selected find 1st item to show alert text message,
  const [items, setItems] = useState<ActionItem[]>(actionsList ?? []);

  useEffect(() => {
    const safe = Array.isArray(tpSelected) ? tpSelected : [];
    const mapped = safe
      .map((action) => {
        const matched = (actionsList ?? []).find(
          (x) => x?.action_type === action?.action_type
        );
        return matched ? { ...action, ...matched } : null;
      })
      .filter((item): item is ActionItem => item !== null);
    setItems(mapped);
  }, [tpSelected, actionsList]);

  const previewRecommendedAction = useMemo(() => {
    // SAP/NAD banner text now carries the full "Please contact the transport
    // provider to" bridge (like every other alert), and fire_alert appends the
    // recommended action verb phrase directly. Do not prepend a "{Contact} to"
    // prefix here or the preview double-renders the bridge.
    return recommendedAction || '';
  }, [recommendedAction]);

  const previewBannerText = useMemo(() => {
    let templateString = currentAlert?.banner_text || '';
    if (
      !IS_TP_EXCEPTION &&
      currentAlert?.alert_type === 'vehicle_idle_in_transit'
    ) {
      templateString = templateString.replace('mins', 'hrs');
    }

    if (IS_TP_EXCEPTION && selectedAction === 'gather_information') {
      templateString += ' gather more information about the issue.';
    } else if (IS_TP_EXCEPTION && selectedAction === 'resolve_issue') {
      if (items?.length > 0) {
        templateString += ` ${items[0]?.title?.toLocaleLowerCase() ?? ''}`;
      } else {
        templateString += ' resolve the issue.';
      }
    }

    if (IS_TEMPERATURE_EXCURSION && Number(previewFormValues.threshold) === 0) {
      templateString = templateString.replace(
        / for over (\{\{threshold_value\}\}|\[threshold\]) mins/g,
        ''
      );
    }

    let resolved = getBannerText(templateString, {
      threshold: previewFormValues.threshold,
      recommendedContacts: previewFormValues.recommendedContacts,
    });

    if (IS_TEMPERATURE_EXCURSION) {
      resolved = resolved.replace(
        /\[threshold\]/g,
        String(
          previewFormValues.threshold ?? currentAlert?.threshold_value ?? ''
        )
      );
    }

    return resolved;
  }, [
    currentAlert,
    previewFormValues,
    getBannerText,
    IS_TP_EXCEPTION,
    IS_TEMPERATURE_EXCURSION,
    items,
    selectedAction,
  ]);

  useEffect(() => {
    if (!currentAlert) return;
    const threshold2Value =
      currentAlert?.alert_type === 'vehicle_idle_in_transit'
        ? Number(currentAlert?.threshold_value_2) / 60
        : (currentAlert?.threshold_value_2 ?? '');
    form.setFieldsValue({
      threshold:
        currentAlert?.alert_type === 'vehicle_idle_in_transit'
          ? Number(currentAlert?.threshold_value) / 60
          : (currentAlert?.threshold_value ?? ''),
      threshold2: threshold2Value,
      recommendedContacts: currentAlert.recommended_contacts?.map(
        (c) => c.id || c.value
      ),
      recommendedAction: currentAlert.recommended_action || '',
      ...(currentAlert.alert_type === 'sensor_location_still_at_pickup'
        ? {
            departureGracePlanned:
              currentAlert?.advanced_settings?.departure_grace_planned_mins ??
              60,
            gpsAccuracyPadding:
              currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
          }
        : currentAlert.alert_type === 'sensor_location_not_at_drop'
          ? {
              arrivalGracePlanned:
                currentAlert?.advanced_settings?.arrival_grace_planned_mins ??
                15,
              stopCheckActual:
                currentAlert?.advanced_settings?.stop_check_actual_mins ?? 0,
              stopCheckPlanned:
                currentAlert?.advanced_settings?.stop_check_planned_mins ?? 0,
              gpsAccuracyPadding:
                currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
            }
          : currentAlert.alert_type === 'temperature_excursion_detected'
            ? {
                lowerBoundPadding:
                  currentAlert?.advanced_settings
                    ?.lower_bounds_degree_padding ?? 0,
                upperBoundPadding:
                  currentAlert?.advanced_settings
                    ?.upper_bounds_degree_padding ?? 0,
                prePickupSkip:
                  currentAlert?.advanced_settings?.pre_collection_skip_count ??
                  0,
              }
            : currentAlert.alert_type === 'temperature_excursion_pre_alarm'
              ? {
                  lowerBoundPadding:
                    currentAlert?.advanced_settings
                      ?.lower_bounds_degree_padding ?? 0,
                  upperBoundPadding:
                    currentAlert?.advanced_settings
                      ?.upper_bounds_degree_padding ?? 0,
                  prePickupSkip:
                    currentAlert?.advanced_settings
                      ?.pre_collection_skip_count ?? 3,
                  rollingAverageHours:
                    currentAlert?.advanced_settings?.rolling_average_hours ?? 5,
                  rateOfChangeFactor:
                    currentAlert?.advanced_settings?.rate_of_change_factor ?? 1,
                  sensorDataStartDelay:
                    currentAlert?.advanced_settings
                      ?.sensor_data_start_delay_minutes ?? 300,
                }
              : {
                  departureGracePlanned: undefined,
                  arrivalGracePlanned: undefined,
                  stopCheckActual: undefined,
                  stopCheckPlanned: undefined,
                  gpsAccuracyPadding: undefined,
                  lowerBoundPadding: undefined,
                  upperBoundPadding: undefined,
                  prePickupSkip: undefined,
                }),
    });

    setPreviewFormValues({
      threshold:
        currentAlert?.alert_type === 'vehicle_idle_in_transit'
          ? Number(currentAlert?.threshold_value) / 60
          : (currentAlert?.threshold_value ?? ''),
      threshold2: threshold2Value,
      recommendedContacts: currentAlert.recommended_contacts?.map(
        (c) => c.id || c.value
      ),
      recommendedAction: currentAlert.recommended_action || '',
      ...(currentAlert.alert_type === 'sensor_location_still_at_pickup'
        ? {
            departureGracePlanned:
              currentAlert?.advanced_settings?.departure_grace_planned_mins ??
              60,
            gpsAccuracyPadding:
              currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
          }
        : currentAlert.alert_type === 'sensor_location_not_at_drop'
          ? {
              arrivalGracePlanned:
                currentAlert?.advanced_settings?.arrival_grace_planned_mins ??
                15,
              stopCheckActual:
                currentAlert?.advanced_settings?.stop_check_actual_mins ?? 0,
              stopCheckPlanned:
                currentAlert?.advanced_settings?.stop_check_planned_mins ?? 0,
              gpsAccuracyPadding:
                currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
            }
          : currentAlert.alert_type === 'temperature_excursion_detected'
            ? {
                lowerBoundPadding:
                  currentAlert?.advanced_settings
                    ?.lower_bounds_degree_padding ?? 0,
                upperBoundPadding:
                  currentAlert?.advanced_settings
                    ?.upper_bounds_degree_padding ?? 0,
                prePickupSkip:
                  currentAlert?.advanced_settings?.pre_collection_skip_count ??
                  0,
              }
            : currentAlert.alert_type === 'temperature_excursion_pre_alarm'
              ? {
                  lowerBoundPadding:
                    currentAlert?.advanced_settings
                      ?.lower_bounds_degree_padding ?? 0,
                  upperBoundPadding:
                    currentAlert?.advanced_settings
                      ?.upper_bounds_degree_padding ?? 0,
                  prePickupSkip:
                    currentAlert?.advanced_settings
                      ?.pre_collection_skip_count ?? 3,
                  rollingAverageHours:
                    currentAlert?.advanced_settings?.rolling_average_hours ?? 5,
                  rateOfChangeFactor:
                    currentAlert?.advanced_settings?.rate_of_change_factor ?? 1,
                  sensorDataStartDelay:
                    currentAlert?.advanced_settings
                      ?.sensor_data_start_delay_minutes ?? 300,
                }
              : {
                  departureGracePlanned: undefined,
                  arrivalGracePlanned: undefined,
                  stopCheckActual: undefined,
                  stopCheckPlanned: undefined,
                  gpsAccuracyPadding: undefined,
                  lowerBoundPadding: undefined,
                  upperBoundPadding: undefined,
                  prePickupSkip: undefined,
                }),
    });
    setIsChecked(currentAlert.is_enabled || false);
  }, [currentAlert, form]);

  const toggleDrawer = (val: string) => {
    setDrawerState?.(val);
  };
  const [initialValues, setInitialValues] = useState<FormValues>({
    threshold: '',
    recommendedContacts: [],
    recommendedAction: '',
  });
  useEffect(() => {
    if (!currentAlert) return;
    const init: FormValues = {
      threshold:
        currentAlert?.alert_type === 'vehicle_idle_in_transit'
          ? Number(currentAlert?.threshold_value) / 60
          : (currentAlert?.threshold_value ?? ''),
      threshold2:
        currentAlert?.alert_type === 'vehicle_idle_in_transit'
          ? Number(currentAlert?.threshold_value_2) / 60
          : (currentAlert?.threshold_value_2 ?? ''),
      recommendedContacts:
        currentAlert.recommended_contacts
          ?.map((c) => c.id || c.value)
          .filter(isDefined) || [],
      recommendedAction: currentAlert.recommended_action || '',
      // Advanced-settings fields are rendered only for location sensor alerts.
      // They must be included here so the dirty-check baseline matches the live form values
      // returned by form.getFieldsValue(); omitting them causes the form to always appear
      // dirty on first load for that alert type.
      ...(currentAlert.alert_type === 'sensor_location_still_at_pickup'
        ? {
            departureGracePlanned:
              currentAlert?.advanced_settings?.departure_grace_planned_mins ??
              60,
            gpsAccuracyPadding:
              currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
          }
        : currentAlert.alert_type === 'sensor_location_not_at_drop'
          ? {
              arrivalGracePlanned:
                currentAlert?.advanced_settings?.arrival_grace_planned_mins ??
                15,
              stopCheckActual:
                currentAlert?.advanced_settings?.stop_check_actual_mins ?? 0,
              stopCheckPlanned:
                currentAlert?.advanced_settings?.stop_check_planned_mins ?? 0,
              gpsAccuracyPadding:
                currentAlert?.advanced_settings?.gps_accuracy_padding ?? 0.5,
            }
          : currentAlert.alert_type === 'temperature_excursion_detected'
            ? {
                lowerBoundPadding:
                  currentAlert?.advanced_settings
                    ?.lower_bounds_degree_padding ?? 0,
                upperBoundPadding:
                  currentAlert?.advanced_settings
                    ?.upper_bounds_degree_padding ?? 0,
                prePickupSkip:
                  currentAlert?.advanced_settings?.pre_collection_skip_count ??
                  0,
              }
            : currentAlert.alert_type === 'temperature_excursion_pre_alarm'
              ? {
                  lowerBoundPadding:
                    currentAlert?.advanced_settings
                      ?.lower_bounds_degree_padding ?? 0,
                  upperBoundPadding:
                    currentAlert?.advanced_settings
                      ?.upper_bounds_degree_padding ?? 0,
                  prePickupSkip:
                    currentAlert?.advanced_settings
                      ?.pre_collection_skip_count ?? 3,
                  rollingAverageHours:
                    currentAlert?.advanced_settings?.rolling_average_hours ?? 5,
                  rateOfChangeFactor:
                    currentAlert?.advanced_settings?.rate_of_change_factor ?? 1,
                  sensorDataStartDelay:
                    currentAlert?.advanced_settings
                      ?.sensor_data_start_delay_minutes ?? 300,
                }
              : {
                  departureGracePlanned: undefined,
                  arrivalGracePlanned: undefined,
                  stopCheckActual: undefined,
                  stopCheckPlanned: undefined,
                  gpsAccuracyPadding: undefined,
                  lowerBoundPadding: undefined,
                  upperBoundPadding: undefined,
                  prePickupSkip: undefined,
                }),
    };

    setInitialValues(init);
    form.setFieldsValue(init);
    setPreviewFormValues(init);
  }, [currentAlert, form]);

  const [pendingNav, setPendingNav] = useState<null | 'next' | 'prev'>(null);

  const handleConfirmDiscard = () => {
    form.resetFields();
    setIsDirty?.(false);
    setShowDiscardModal?.(false);

    if (pendingNav === 'next') goNext();
    if (pendingNav === 'prev') goPrev();
    if (!pendingNav) {
      setShowDrawer?.(false);
    }
    setPendingNav(null);
  };

  // to show confirmation modal on action change:
  const [initialTpSelected, setInitialTpSelected] = useState<ActionItem[]>([]);

  useEffect(() => {
    if (currentAlert?.preferred_actions) {
      const initial = (currentAlert.preferred_actions || []).filter(
        (item) => item.id != null
      );
      setTpSelected?.(initial);
      setInitialTpSelected(initial);
    }
  }, [currentAlert, setTpSelected]);
  useEffect(() => {
    if (!IS_TP_EXCEPTION) return;
    if (!tpSelected || !initialTpSelected) return;
    const lengthChanged = tpSelected.length !== initialTpSelected.length;
    const contentChanged =
      !lengthChanged &&
      tpSelected.some(
        (item, idx) => item.action_type !== initialTpSelected[idx]?.action_type
      );
    setIsDirty?.(lengthChanged || contentChanged);
  }, [tpSelected, initialTpSelected, setIsDirty, IS_TP_EXCEPTION]);

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        onValuesChange={(changed, all) => {
          setPreviewFormValues(all);
          const dirty = JSON.stringify(all) !== JSON.stringify(initialValues);
          setIsDirty?.(dirty);
        }}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        initialValues={{
          // threshold: currentAlert?.threshold_value || '',
          recommendedContacts: currentAlert?.recommended_contacts?.map(
            (item) => item.id || item.value
          ),
          recommendedAction: currentAlert?.recommended_action || '',
        }}
        className='alert-customization-form'
      >
        {/* header  */}
        <div ref={divRef} className='drawer-title-header'>
          <div className='alert-customization-form-header-actions'>
            {currentAlert?.changelog?.length > 0 && (
              <Tooltip
                title={<ChangelogContent />}
                placement='bottom'
                style={{ width: '300px' }}
                classNames={{
                  root: 'changelog-tooltip-overlay',
                }}
                overlayStyle={{ minWidth: '296px' }}
                trigger='hover'
              >
                <span className='cursor-pointer h-4'>
                  <ChangelogIcon />
                </span>
              </Tooltip>
            )}
            <span
              onClick={() => setShowResetModal(true)}
              className={`alert-customization-form-header-icon cursor-pointer h-4 ${currentAlert?.default_threshold == currentAlert?.threshold_value ? 'disabled-row' : ''}`}
            >
              <ResetIcon />
            </span>
            <Flex gap={20} className='home-chart-detail-drawer-header-actions'>
              <span
                className={`alert-customization-form-header-icon arrow-icon h-4 ${currentIndex <= 0 ? 'disabled-row' : ''}`}
                onClick={() => currentIndex > 0 && handlePrevious()}
              >
                <LeftArrowIcon />
              </span>

              <span
                className={`arrow-icon h-4 ${
                  currentIndex >= allClickableAlerts.length - 1
                    ? 'disabled-row'
                    : ''
                }`}
                onClick={() =>
                  currentIndex < allClickableAlerts.length - 1 && handleNext()
                }
              >
                <RightArrowIcon />
              </span>
            </Flex>
          </div>

          <CustomTitle
            text={currentAlert?.phase_display || '--'}
            fontSize={12}
            fontWeight='700'
            lineHeight='16px'
            color='var(--text-secondary)'
            marginBottom={0}
            style={{ width: 'fit-content' }}
          />
          <Flex
            justify='space-between'
            align='center'
            className='drawer-header-margin-top-bottom'
          >
            <Flex gap={6} align='center'>
              <TruncateWithTooltip
                text={String(title || '')}
                max={32}
                render={(t) => (
                  <CustomTitle
                    text={t}
                    fontSize={20}
                    fontWeight='700'
                    lineHeight='28px'
                    color='var(--text-primary)'
                    marginBottom={0}
                    style={{ width: 'fit-content' }}
                  />
                )}
              />

              {currentAlert.sensor_status !== 'setup_required' && (
                <>
                  {/* Case 1: sensor_required == 'required' */}
                  {currentAlert.sensor_required === 'required' && (
                    <Tag
                      icon={isChecked ? <SignalIcon /> : <OffIcon />}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        backgroundColor: isChecked
                          ? 'var(--thin-green)'
                          : 'var(--danger-thin)',
                        color: isChecked
                          ? 'var(--live-tag-text)'
                          : 'var(--sensor-danger-text)',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: 4,
                        padding: '2px 4px',
                        fontSize: 10,
                        lineHeight: '16px',
                      }}
                    >
                      {isChecked ? 'Enabled' : 'Off'}
                    </Tag>
                  )}
                  {/* Case 2: sensor_required == 'none' AND is_enabled is false */}
                  {currentAlert.sensor_required === 'none' &&
                    isChecked === false && (
                      <Tag
                        icon={<OffIcon />}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          backgroundColor: 'var(--danger-thin)',
                          color: 'var(--sensor-danger-text)',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: 4,
                          padding: '2px 4px',
                          fontSize: 10,
                          lineHeight: '16px',
                        }}
                      >
                        {'Off'}
                      </Tag>
                    )}
                </>
              )}
            </Flex>
            <Flex gap={16} align='center'>
              <ToggleSwitch
                label=''
                checked={isChecked}
                onChange={handleToggle}
              />
            </Flex>
          </Flex>

          <CustomParagraph
            text={currentAlert?.description}
            noText='--'
            fontSize='14px'
            lineHeight='20px'
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
          <Divider style={{ marginTop: '16px', marginBottom: '0' }} />
        </div>

        <div
          // eslint-disable-next-line react/forbid-dom-props -- top padding tracks the measured header height at run time
          style={{ paddingTop: `${divHeight - 76 + 60}px` }}
          className='alert-customization-form-body custom-scrollbar'
        >
          <Row
            gutter={[16, 16]}
            style={{
              opacity: isChecked ? 1 : '0.5',
              pointerEvents: isChecked ? 'auto' : 'none',
            }}
          >
            {/* Threshold */}
            {!IS_TP_EXCEPTION && (
              <>
                {IS_SENSOR_NOT_AT_DROP ? (
                  <>
                    <Col span={24}>
                      <Form.Item
                        label='Distance threshold'
                        name='threshold'
                        className='custom-input form-input-style custom-input-focus-ring'
                        rules={[
                          { required: true, message: 'Please enter threshold' },
                          {
                            pattern: /^\d+$/,
                            message: 'Only numbers are allowed',
                          },
                          {
                            validator: (_, value) => {
                              const num = Number(value);
                              if (Number.isNaN(num) || num < 0 || num > 100) {
                                return Promise.reject(
                                  new Error(
                                    'Distance must be between 0 and 100 mi'
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        extra='Fires when every reading from a sensor inside the window is beyond this distance. One stray far reading never alerts on its own.'
                      >
                        <CustomInput
                          bgColor='var(--light-gray)'
                          height='36px'
                          borderRadius='8px'
                          border='none'
                          suffix='mi'
                          prefix='Beyond'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label='Arrival grace period'
                        name='threshold2'
                        className='custom-input form-input-style custom-input-focus-ring'
                        rules={[
                          {
                            required: true,
                            message: 'Please enter grace period',
                          },
                          {
                            pattern: /^\d+$/,
                            message: 'Only numbers are allowed',
                          },
                        ]}
                        extra="Wait this long after the flight's actual arrival before checking logger position"
                      >
                        <CustomInput
                          bgColor='var(--light-gray)'
                          height='36px'
                          borderRadius='8px'
                          border='none'
                          suffix='mins'
                          prefix='After'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <AdvancedSettingsToggle
                        isOpen={advancedSettingsOpen}
                        onToggle={() =>
                          setAdvancedSettingsOpen((open) => !open)
                        }
                      >
                        <Form.Item
                          label='Arrival grace period (vs. planned/latest time)'
                          name='arrivalGracePlanned'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter planned grace period',
                            },
                            {
                              pattern: /^\d+$/,
                              message: 'Only numbers are allowed',
                            },
                          ]}
                          extra='Used when actual arrival time is unavailable'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='mins'
                            prefix='After'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                        <Form.Item
                          label='Stop checking at (vs. actual next pickup)'
                          name='stopCheckActual'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter stop-check offset',
                            },
                            {
                              pattern: /^\d+$/,
                              message: 'Only numbers are allowed',
                            },
                          ]}
                          extra="Closes the check window once the cargo's next leg begins"
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='mins'
                            prefix='Next pickup +'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                        <Form.Item
                          label='Stop checking at (vs. planned/latest next pickup)'
                          name='stopCheckPlanned'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter planned stop-check offset',
                            },
                            {
                              pattern: /^\d+$/,
                              message: 'Only numbers are allowed',
                            },
                          ]}
                          extra='Used when the actual next pickup time is unavailable'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='mins'
                            prefix='Next pickup +'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                        <Form.Item
                          label='GPS accuracy padding'
                          name='gpsAccuracyPadding'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter GPS accuracy padding',
                            },
                            {
                              pattern: /^\d+(\.\d+)?$/,
                              message: 'Only numbers and decimals are allowed',
                            },
                            {
                              validator: (_, value) => {
                                if (
                                  value === undefined ||
                                  value === '' ||
                                  value === null
                                ) {
                                  return Promise.resolve();
                                }
                                const num = Number(value);
                                if (Number.isNaN(num) || num < 0) {
                                  return Promise.reject(
                                    new Error(
                                      'GPS accuracy padding must be a non-negative number'
                                    )
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                          extra='Multiplier applied to the GPS accuracy value (in meters) to pad the distance threshold'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='x reported accuracy'
                            prefix='Multiply by'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                      </AdvancedSettingsToggle>
                    </Col>
                  </>
                ) : IS_SENSOR_STILL_AT_PICKUP ? (
                  <>
                    <Col span={24}>
                      <Form.Item
                        label='Distance threshold'
                        name='threshold'
                        className='custom-input form-input-style custom-input-focus-ring'
                        rules={[
                          { required: true, message: 'Please enter threshold' },
                          {
                            pattern: /^\d+$/,
                            message: 'Only numbers are allowed',
                          },
                          {
                            validator: (_, value) => {
                              const num = Number(value);
                              if (Number.isNaN(num) || num < 0 || num > 100) {
                                return Promise.reject(
                                  new Error(
                                    'Distance must be between 0 and 100 mi'
                                  )
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        extra="Fires when any sensor's last known position is within this distance of pickup"
                      >
                        <CustomInput
                          bgColor='var(--light-gray)'
                          height='36px'
                          borderRadius='8px'
                          border='none'
                          suffix='mi'
                          prefix='Within'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label='Departure grace period'
                        name='threshold2'
                        className='custom-input form-input-style custom-input-focus-ring'
                        rules={[
                          {
                            required: true,
                            message: 'Please enter grace period',
                          },
                          {
                            pattern: /^\d+$/,
                            message: 'Only numbers are allowed',
                          },
                        ]}
                        extra="Wait this long after the flight's actual departure before checking logger position"
                      >
                        <CustomInput
                          bgColor='var(--light-gray)'
                          height='36px'
                          borderRadius='8px'
                          border='none'
                          suffix='mins'
                          prefix='After'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <AdvancedSettingsToggle
                        isOpen={advancedSettingsOpen}
                        onToggle={() =>
                          setAdvancedSettingsOpen((open) => !open)
                        }
                      >
                        <Form.Item
                          label='Departure grace period (vs. planned time)'
                          name='departureGracePlanned'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter planned grace period',
                            },
                            {
                              pattern: /^\d+$/,
                              message: 'Only numbers are allowed',
                            },
                          ]}
                          extra='Used when actual departure time is unavailable'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='mins'
                            prefix='After'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                        <Form.Item
                          label='GPS accuracy padding'
                          name='gpsAccuracyPadding'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              required: true,
                              message: 'Please enter GPS accuracy padding',
                            },
                            {
                              pattern: /^\d+(\.\d+)?$/,
                              message: 'Only numbers and decimals are allowed',
                            },
                            {
                              validator: (_, value) => {
                                if (
                                  value === undefined ||
                                  value === '' ||
                                  value === null
                                ) {
                                  return Promise.resolve();
                                }
                                const num = Number(value);
                                if (Number.isNaN(num) || num < 0) {
                                  return Promise.reject(
                                    new Error(
                                      'GPS accuracy padding must be a non-negative number'
                                    )
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                          extra='Multiplier applied to the GPS accuracy value (in meters) to pad the distance threshold'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix='x reported accuracy'
                            prefix='Multiply by'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                      </AdvancedSettingsToggle>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col span={24}>
                      <Form.Item
                        label={
                          IS_TEMPERATURE_PRE_ALARM
                            ? 'Projection window'
                            : 'Threshold'
                        }
                        name='threshold'
                        className='custom-input form-input-style custom-input-focus-ring'
                        rules={
                          currentAlert?.default_threshold === 'N/A'
                            ? [] // no validation rules when disabled
                            : [
                                {
                                  required:
                                    currentAlert?.default_threshold === 'N/A'
                                      ? false
                                      : true,
                                  message: 'Please enter threshold',
                                },
                                {
                                  pattern:
                                    currentAlert?.alert_type ===
                                      'vehicle_idle_in_transit' ||
                                    currentAlert?.alert_type ===
                                      'delivery_overdue'
                                      ? /^\d*(\.\d*)?$/
                                      : /^\d+$/,
                                  message:
                                    currentAlert?.alert_type ===
                                      'vehicle_idle_in_transit' ||
                                    currentAlert?.alert_type ===
                                      'delivery_overdue'
                                      ? 'Only numbers and decimals are allowed'
                                      : 'Only numbers are allowed',
                                },
                                () => ({
                                  validator(_, value) {
                                    if (value === undefined || value === '') {
                                      return Promise.resolve();
                                    }

                                    const num = Number(value);
                                    if (isNaN(num))
                                      return Promise.reject('Invalid number');

                                    const min =
                                      currentAlert?.alert_metadata?.min_value;
                                    const max =
                                      currentAlert?.alert_type ===
                                      'vehicle_idle_in_transit'
                                        ? 24
                                        : currentAlert?.alert_metadata
                                            ?.max_value;

                                    if (min != null && num < min)
                                      return Promise.reject(
                                        `Value should be ≥ ${min}`
                                      );
                                    if (max != null && num > max)
                                      return Promise.reject(
                                        `Value should be ≤ ${max}`
                                      );

                                    return Promise.resolve();
                                  },
                                }),
                              ]
                        }
                        extra={
                          IS_TEMPERATURE_PRE_ALARM
                            ? 'No pre-alarm fires while an actual excursion is active'
                            : undefined
                        }
                      >
                        <CustomInput
                          placeholder=''
                          bgColor='var(--light-gray)'
                          height='36px'
                          textColor={
                            currentAlert?.default_threshold === 'N/A'
                              ? '#a1a1a1'
                              : 'var(--text-primary)'
                          }
                          borderRadius='8px'
                          border='none'
                          disabled={currentAlert?.default_threshold === 'N/A'}
                          suffix={
                            currentAlert?.alert_type ===
                            'vehicle_idle_in_transit'
                              ? 'hrs'
                              : currentAlert?.unit || ''
                          }
                          prefix={
                            currentAlert?.threshold_comparison
                              ? currentAlert.threshold_comparison
                                  .charAt(0)
                                  .toUpperCase() +
                                currentAlert.threshold_comparison.slice(1)
                              : ''
                          }
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>
                    {IS_TEMPERATURE_PRE_ALARM && (
                      <Col span={24}>
                        <Form.Item
                          label='Re-trigger interval'
                          name='threshold2'
                          className='custom-input form-input-style custom-input-focus-ring'
                          rules={[
                            {
                              pattern: /^\d+$/,
                              message: 'Only numbers are allowed',
                            },
                          ]}
                          extra='Minimum time before re-alerting for the same logger'
                        >
                          <CustomInput
                            bgColor='var(--light-gray)'
                            height='36px'
                            borderRadius='8px'
                            border='none'
                            suffix={currentAlert?.unit || 'mins'}
                            prefix='Every'
                            placeholderColor='var(--text-secondary)'
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {IS_TEMPERATURE_ADVANCED && (
                      <Col span={24}>
                        <AdvancedSettingsToggle
                          isOpen={advancedSettingsOpen}
                          onToggle={() =>
                            setAdvancedSettingsOpen((open) => !open)
                          }
                          subtitle={
                            IS_TEMPERATURE_PRE_ALARM ? '' : 'Evaluation tuning'
                          }
                        >
                          {IS_TEMPERATURE_PRE_ALARM ? (
                            <>
                              <CustomParagraph
                                text='Calculation'
                                fontSize='13px'
                                fontWeight={600}
                                color='var(--text-secondary)'
                                marginBottom={12}
                              />
                              <Form.Item
                                label='Rolling average window'
                                name='rollingAverageHours'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                                extra='Needs at least 3 hourly segments to project'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='hrs'
                                  prefix='Last'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Rate of change factor'
                                name='rateOfChangeFactor'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                                extra='Applied to each hourly delta. 1.0 uses raw rate; above 1.0 alerts earlier'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  prefix='Multiply by'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Lower bound padding'
                                name='lowerBoundPadding'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='°C'
                                  prefix='Add'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Upper bound padding'
                                name='upperBoundPadding'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='°C'
                                  prefix='Subtract'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <CustomParagraph
                                text='Data-quality gates'
                                fontSize='13px'
                                fontWeight={600}
                                color='var(--text-secondary)'
                                marginBottom={12}
                                marginTop={8}
                              />
                              <Form.Item
                                label='Pre-pickup skip'
                                name='prePickupSkip'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+$/,
                                    message: 'Only numbers are allowed',
                                  },
                                ]}
                                extra='Ignored when the logger starts syncing before Proof of Pickup'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='readings'
                                  prefix='Skip first'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Minimum data history'
                                name='sensorDataStartDelay'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+$/,
                                    message: 'Only numbers are allowed',
                                  },
                                ]}
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='mins'
                                  prefix='After'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                            </>
                          ) : (
                            <>
                              <Form.Item
                                label='Lower bound padding'
                                name='lowerBoundPadding'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                                extra='Raises the minimum bound used for evaluation'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='°C'
                                  prefix='Add'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Upper bound padding'
                                name='upperBoundPadding'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+(\.\d+)?$/,
                                    message:
                                      'Only numbers and decimals are allowed',
                                  },
                                ]}
                                extra='Lowers the maximum bound used for evaluation'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='°C'
                                  prefix='Subtract'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                              <Form.Item
                                label='Pre-pickup skip'
                                name='prePickupSkip'
                                className='custom-input form-input-style custom-input-focus-ring'
                                rules={[
                                  {
                                    pattern: /^\d+$/,
                                    message: 'Only numbers are allowed',
                                  },
                                ]}
                                extra='Skip the first N readings when the logger started syncing before POP'
                              >
                                <CustomInput
                                  bgColor='var(--light-gray)'
                                  height='36px'
                                  borderRadius='8px'
                                  border='none'
                                  suffix='readings'
                                  prefix='Skip first'
                                  placeholderColor='var(--text-secondary)'
                                />
                              </Form.Item>
                            </>
                          )}
                        </AdvancedSettingsToggle>
                      </Col>
                    )}
                    {showThreshold2 &&
                    currentAlert?.alert_name == 'Low battery' ? (
                      <Col span={24}>
                        <Flex gap={10} align='center'>
                          <Form.Item
                            label='Threshold 2'
                            name='threshold2'
                            className='custom-input form-input-style custom-input-focus-ring w-full'
                            rules={
                              currentAlert?.default_threshold === 'N/A'
                                ? [] // no validation rules when disabled
                                : [
                                    {
                                      pattern:
                                        currentAlert?.alert_type ===
                                        'vehicle_idle_in_transit'
                                          ? /^\d*(\.\d*)?$/
                                          : /^\d+$/,
                                      message:
                                        currentAlert?.alert_type ===
                                        'vehicle_idle_in_transit'
                                          ? 'Only numbers and decimals are allowed'
                                          : 'Only numbers are allowed',
                                    },
                                    () => ({
                                      validator(_, value) {
                                        if (
                                          value === undefined ||
                                          value === ''
                                        ) {
                                          return Promise.resolve();
                                        }

                                        const num = Number(value);
                                        if (isNaN(num))
                                          return Promise.reject(
                                            'Invalid number'
                                          );

                                        const min =
                                          currentAlert?.alert_metadata
                                            ?.min_value;
                                        const max =
                                          currentAlert?.alert_type ===
                                          'vehicle_idle_in_transit'
                                            ? 24
                                            : currentAlert?.alert_metadata
                                                ?.max_value;

                                        if (min != null && num < min)
                                          return Promise.reject(
                                            `Value should be ≥ ${min}`
                                          );
                                        if (max != null && num > max)
                                          return Promise.reject(
                                            `Value should be ≤ ${max}`
                                          );

                                        return Promise.resolve();
                                      },
                                    }),
                                  ]
                            }
                          >
                            <CustomInput
                              placeholder=''
                              bgColor='var(--light-gray)'
                              height='36px'
                              textColor={
                                currentAlert?.default_threshold === 'N/A'
                                  ? '#a1a1a1'
                                  : 'var(--text-primary)'
                              }
                              borderRadius='8px'
                              border='none'
                              disabled={
                                currentAlert?.default_threshold === 'N/A'
                              }
                              suffix={
                                currentAlert?.alert_type ===
                                'vehicle_idle_in_transit'
                                  ? 'hrs'
                                  : currentAlert?.unit || ''
                              }
                              prefix={
                                currentAlert?.threshold_comparison
                                  ? currentAlert.threshold_comparison
                                      .charAt(0)
                                      .toUpperCase() +
                                    currentAlert.threshold_comparison.slice(1)
                                  : ''
                              }
                              placeholderColor='var(--text-secondary)'
                            />
                          </Form.Item>
                          <span
                            onClick={() => {
                              form.setFieldValue('threshold2', undefined);
                              setShowThreshold2(false);
                            }}
                            className='mt-6 cursor-pointer'
                          >
                            <DeleteIcon />
                          </span>
                        </Flex>
                      </Col>
                    ) : null}
                    {!showThreshold2 &&
                      currentAlert?.default_threshold !== 'N/A' &&
                      currentAlert?.alert_name == 'Low battery' && (
                        <span className='pl-2'>
                          <AddSelectableItem
                            text='Add 2nd Threshold'
                            onClick={() => setShowThreshold2(true)}
                          />
                        </span>
                      )}
                  </>
                )}
              </>
            )}
            {/* Recipient List */}
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Contact'
                name='recommendedContacts'
                style={{ marginTop: '0px' }}
                rules={[{ required: true, message: 'Please select contact' }]}
              >
                <Select
                  className='gray-tag-select custom-select-focus-ring'
                  mode='multiple'
                  placeholder='Select'
                  allowClear
                  value={selectedContact}
                  onChange={(value) => setSelectedContact(value)}
                  onClear={() => setSelectedContact(null)}
                  optionLabelProp='inputLabel'
                  options={modifiedRecipientList}
                  fieldNames={{ label: 'label', value: 'value' }}
                  style={{
                    width: '100%',
                    height: '36px',
                    backgroundColor: 'var(--light-gray)',
                    borderRadius: '8px',
                  }}
                />
              </Form.Item>
            </Col>

            {/* Radio buttons Recomended action for TP exceptions  */}
            {IS_TP_EXCEPTION && (
              <Col span={24}>
                <CustomParagraph
                  text={'Recommended action'}
                  fontSize='14px'
                  lineHeight='16px'
                  fontWeight={600}
                  color='var(--text-primary)'
                  marginBottom={12}
                />

                <CustomRadioGroup
                  value={selectedAction}
                  onChange={handleActionChange}
                  options={[
                    {
                      value: 'gather_information',
                      label: 'Gather information',
                    },
                    { value: 'resolve_issue', label: 'Resolve the issue' },
                  ]}
                />
              </Col>
            )}

            {/* Text area  */}
            {!IS_TP_EXCEPTION && (
              <Col span={24}>
                <Form.Item
                  className='custom-input form-input-style custom-input-focus-ring'
                  label='Recommended action'
                  name={'recommendedAction'}
                  style={{ marginTop: '0px' }}
                  rules={[
                    {
                      required: true,
                      message: 'Please enter recommended action',
                    },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder=''
                    onFocus={() => setIsActionFocused(true)}
                    onBlur={() => setIsActionFocused(false)}
                    onChange={(e) => setActionChange(e.target?.value)}
                    style={{
                      border: 'none',
                      backgroundColor: 'var(--light-gray)',
                      borderRadius: '8px',
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {/* Sortable TP exceptions selected options */}
            {selectedAction == 'resolve_issue' && IS_TP_EXCEPTION ? (
              <>
                <Divider style={{ margin: 0 }} />
                <Col span={24}>
                  <CustomParagraph
                    text={'Preferred actions for this exception'}
                    fontSize='14px'
                    lineHeight='16px'
                    fontWeight={600}
                    color='var(--text-primary)'
                    marginBottom={4}
                  />
                  <CustomParagraph
                    text={
                      'Drag to change priority in which actions will be attempted'
                    }
                    fontSize='12px'
                    lineHeight='20px'
                    fontWeight={400}
                    color='var(--text-secondary)'
                    marginBottom={0}
                  />
                  {tpSelected && setTpSelected && (
                    <SortableActions
                      actionsList={actionsList}
                      tpSelected={tpSelected}
                      setTpSelected={setTpSelected}
                    />
                  )}
                </Col>
                <Col
                  span={24}
                  className={`${tpSelected?.length === actionsList?.length && 'disabled-row'}`}
                >
                  <AddItemButton
                    marginTop='0px'
                    fontSize='12px'
                    fontWeight='600'
                    text={`Add option`}
                    onClick={() => {
                      toggleDrawer('actions');
                    }}
                  />
                </Col>
              </>
            ) : null}

            {/* Example alert  */}
            {isChecked && (
              <>
                <Divider style={{ margin: '0' }} />
                <Col span={24}>
                  <Form.Item
                    className='custom-input form-input-style'
                    label='Example alert'
                    style={{ marginTop: '0px' }}
                  >
                    <AlertCardPreview
                      cardStyle={'style-2'}
                      icon={<WarningFilled />}
                      title={title || ''}
                      createdAt={''}
                      description={previewBannerText}
                      recommendedAction={previewRecommendedAction}
                      isActionFocused={isActionFocused}
                      bgColor={
                        currentAlert?.is_critical_type ? '#DE1135' : '#F6BC2F'
                      }
                      color={currentAlert?.is_critical_type ? '#fff' : '#000'}
                      actionButtonText={'Mark as Resolved'}
                      actionButtonBorderRadius='8px'
                      actionButtonBgColor={
                        currentAlert?.is_critical_type ? '#BB032A' : '#FBE5B6'
                      }
                      IS_TP_EXCEPTION={IS_TP_EXCEPTION}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <div className='drawer-custom-footer'>
            <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
              <CustomButton
                key='button1'
                text='Save'
                bgColor='#000'
                htmlType='submit'
                color='#fff'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
                height='48px'
                width='100%'
              />

              <CustomButton
                key='button2'
                text='Cancel'
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='16px'
                fontWeight='600'
                boxShadow='none'
                height='auto'
                width='100%'
                padding={'11.5px 0px 12px 0px'}
                border='none'
                marginTop='0px'
                onClick={() => {
                  setShowDrawer?.(false);
                  setIsDirty?.(false);
                }}
              />
            </Row>
          </div>
        </div>
      </Form>

      <CustomDeleteModal
        visible={showResetModal}
        onConfirm={() => handleConfirmReset()}
        onClose={() => setShowResetModal(false)}
        title={`Reset to default`}
        message={`Are you sure you want to reset this alert to its default settings? This cannot be undone.`}
        cancelText={'Cancel'}
        confirmText={'Reset'}
        confirmColor={`#000`}
      />
      {/* discard changes modal  */}
      <CustomDeleteModal
        visible={showDiscardModal ?? false}
        onConfirm={() => handleConfirmDiscard()}
        onClose={() => {
          setPendingNav(null);
          setShowDiscardModal?.(false);
        }}
        title={`Discard changes?`}
        message={``}
        cancelText={'Keep editing'}
        confirmText={'Discard'}
        confirmColor={`#000`}
      />
    </>
  );
};

export default AlertCustomizationForm;
